// components/ta/TAQueueScreen.js — the Thermal Workbench.
// One queue for both thermal types (Thermal Analysis + Thermal Modeling) sharing a single priority
// order; the server's /projects/ta-queue endpoint returns both. File name predates TM.
import React, { useState, useEffect, useCallback } from 'react';
import WorkflowModal from '../projects/WorkflowModal';
import NewProjectModal from '../projects/NewProjectModal';
import { WorkflowEngine } from '../../services/workflowEngine';
import { THERMAL_QUEUE_TYPES, getProjectTypeDisplay } from '../../config/workflows';
import { useAlert } from '../common/AppAlert';
import { formatDate, daysBetween, totalHoldDaysWithinInterval } from '../../utils/dates';

const URGENT_CATEGORY = 'Urgent Engineering Support';
// Short label for the Type column ("Thermal Analysis" -> "TA", "Thermal Modeling" -> "TM")
const typeAbbrev = (type) => getProjectTypeDisplay(type).split(' ').map(w => w[0]).join('');

// Inline priority editor — saves on blur or Enter, optimistic update with revert on failure
const PriorityCell = ({ projectId, value, token, api, onSaved }) => {
  const { alert } = useAlert();
  const [local, setLocal] = useState(value || '');
  useEffect(() => { setLocal(value || ''); }, [value]);

  const save = async () => {
    const next = local.trim();
    if (next === (value || '')) return;
    try {
      await api.put(`/projects/${projectId}/priority`, { priority: next }, token);
      if (onSaved) onSaved();
    } catch (e) {
      setLocal(value || '');
      alert('Failed to update priority: ' + e.message);
    }
  };

  return (
    <input
      className="priority-input"
      value={local}
      onChange={(e) => setLocal(e.target.value.slice(0, 3))}
      onBlur={save}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      maxLength={3}
    />
  );
};

const TAQueueScreen = ({ user, token, onBack, api }) => {
  const { alert, confirm, prompt } = useAlert();
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState('');
  const [filterString, setFilterString] = useState('');
  const [showPendingPrereqs, setShowPendingPrereqs] = useState(false);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('ASC');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortDirection('ASC');
    }
  };

  const sortIndicator = (field) => {
    if (sortField === field) return sortDirection === 'ASC' ? ' ▲' : ' ▼';
    return '';
  };

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const data = await api.get('/projects/ta-queue', token);
      const projectsWithStatus = data.map(p => ({
        ...p,
        current_status: WorkflowEngine.getProjectStatus(p.project_type, p.completed_tasks || [])
      }));
      setProjects(projectsWithStatus);
      setError('');
    } catch (e) {
      setError('Failed to load thermal queue: ' + e.message);
    }
  }, [token, api]);

  useEffect(() => { load(); }, [load]);

  const startHold = async (projectId) => {
    const reason = await prompt('Reason for hold (required):', 'Place Project on Hold');
    if (reason === null) return;
    if (!reason.trim()) {
      alert('Reason is required.');
      return;
    }
    try {
      await api.post(`/projects/${projectId}/holds`, { reason: reason.trim() }, token);
      load();
    } catch (e) {
      alert('Failed to start hold: ' + e.message);
    }
  };

  const endHold = async (projectId, holdId) => {
    if (!(await confirm('End this hold and resume the project?'))) return;
    try {
      await api.put(`/projects/${projectId}/holds/${holdId}/end`, {}, token);
      load();
    } catch (e) {
      alert('Failed to end hold: ' + e.message);
    }
  };

  const exportToExcel = async () => {
    const filteredIds = filtered.map(p => p.id);
    if (filteredIds.length === 0) {
      await alert('No projects in the current view to export.');
      return;
    }
    try {
      const response = await fetch('/api/projects/ta-queue/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ projectIds: filteredIds })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${response.status}`);
      }
      const refreshed = response.headers.get('X-Refreshed-Token');
      if (refreshed) localStorage.setItem('gtrack_token', refreshed);

      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      if (!match) throw new Error('Server did not return a filename');
      const filename = match[1];

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      await alert('Failed to export: ' + e.message);
    }
  };

  const handleFilterStringKeyPress = (e) => {
    if (e.key === 'Enter') setFilterString(e.target.value);
  };

  const filtered = projects.filter(p => {
    if (!showPendingPrereqs && !p.prereqs_completed_at) return false;
    // WorkflowEngine returns "<lastTaskId> - Complete" (capital C) only when ALL tasks are done.
    // Mid-workflow status strings can contain "complete" inside a task name (e.g.
    // "5 - All Pre-Reqs complete"); endsWith(' - Complete') matches only the true completion.
    if (!includeCompleted && (p.current_status || '').endsWith(' - Complete')) return false;
    if (filterString) {
      const lf = filterString.toLowerCase();
      const matches = Object.values(p).some(v =>
        v !== null && (typeof v === 'string' || typeof v === 'number') && String(v).toLowerCase().includes(lf)
      );
      if (!matches) return false;
    }
    return true;
  });

  // Sort: pulls the comparable value for each project given the active sort field.
  // Unsortable/missing values sort last (Infinity / -Infinity per direction intent).
  const sortValueOf = (p, field) => {
    const m = p.metadata || {};
    switch (field) {
      case 'priority': {
        const v = (p.priority || '').trim();
        return v && /^\d+$/.test(v) ? parseInt(v) : Infinity;
      }
      case 'project_created': return new Date(p.project_created).getTime();
      case 'project_type': return typeAbbrev(p.project_type);
      case 'request_category': return m.request_category?.value === URGENT_CATEGORY ? 0 : 1;
      case 'project_name': return (p.project_name || '').toLowerCase();
      case 'current_status': {
        // Status strings are "N - description"; extract leading step index for numeric sort
        const m2 = (p.current_status || '').match(/^(\d+)/);
        return m2 ? parseInt(m2[1]) : 999;
      }
      case 'num_measurements': return parseFloat(m.num_measurements?.value) || -Infinity;
      case 'est_meas_time_days': return parseFloat(m.est_meas_time_days?.value) || -Infinity;
      case 'meas_start_date': return m.meas_start_date?.value ? new Date(m.meas_start_date.value).getTime() : -Infinity;
      case 'ta_report_completion_date': return m.ta_report_completion_date?.value ? new Date(m.ta_report_completion_date.value).getTime() : -Infinity;
      case 'since_created': return daysBetween(p.project_created, m.meas_start_date?.value || new Date()) ?? -Infinity;
      case 'since_prereqs':
        return p.prereqs_completed_at
          ? (daysBetween(p.prereqs_completed_at, m.meas_start_date?.value || new Date()) ?? -Infinity)
          : -Infinity;
      case 'active_meas_time': {
        const measStart = m.meas_start_date?.value;
        if (!measStart) return -Infinity;
        const intervalEnd = m.ta_report_completion_date?.value || new Date();
        const intervalDays = daysBetween(measStart, intervalEnd);
        const holdDays = totalHoldDaysWithinInterval(p.holds || [], measStart, intervalEnd);
        return Math.max(0, intervalDays - holdDays);
      }
      case 'avg_meas_time': {
        const measStart = m.meas_start_date?.value;
        const numMeas = parseFloat(m.num_measurements?.value);
        if (!measStart || !numMeas) return -Infinity;
        const intervalEnd = m.ta_report_completion_date?.value || new Date();
        const intervalDays = daysBetween(measStart, intervalEnd);
        const holdDays = totalHoldDaysWithinInterval(p.holds || [], measStart, intervalEnd);
        return Math.max(0, intervalDays - holdDays) / numMeas;
      }
      default: return 0;
    }
  };

  const displayed = sortField
    ? [...filtered].sort((a, b) => {
        const av = sortValueOf(a, sortField);
        const bv = sortValueOf(b, sortField);
        if (av < bv) return sortDirection === 'ASC' ? -1 : 1;
        if (av > bv) return sortDirection === 'ASC' ? 1 : -1;
        return 0;
      })
    : filtered;

  return (
    <div className="app-root">
      <div className="topbar">
        <div className="topbar-left">Logged in as: {user.displayName}</div>
        <h1 className="topbar-center">GTrack Thermal Workbench</h1>
        <div className="topbar-right">
          <button className="btn" onClick={onBack}>← GTrack Dashboard</button>
        </div>
      </div>

      <div className="breadcrumb">
        <a onClick={onBack} style={{ cursor: 'pointer' }}>GTrack Dashboard</a>
        <span className="sep">/</span>
        <span className="current">Thermal Workbench</span>
      </div>

      <div className="controls">
        <input
          className="input"
          placeholder="Filter String (press Enter)"
          defaultValue={filterString}
          onKeyPress={handleFilterStringKeyPress}
        />
        <button className="btn" onClick={() => setFilterString('')}>Clear Filter</button>
        <button
          className={`btn btn-create push-right${!user.isAdmin ? ' faux-disabled' : ''}`}
          onClick={() => user.isAdmin ? setShowNewProject(true) : alert('Must be an administrator to create new projects.')}
        >
          + Add New Request
        </button>
        <button className="btn" onClick={exportToExcel} title="Download the visible projects as an .xlsx file">
          Export to Excel
        </button>
        <button
          className="btn faux-disabled"
          title="Phase 2 — Excel import will be enabled once the measurement spreadsheet format stabilizes"
        >
          Import from Excel
        </button>
      </div>

      <div className="filter-status">
        <strong>Showing:</strong> Pre-reqs complete &amp; not yet completed (default working queue).
        <a className={showPendingPrereqs ? 'toggle-active' : ''}
           onClick={() => setShowPendingPrereqs(!showPendingPrereqs)}>
          {showPendingPrereqs ? '✓ ' : ''}Show pending pre-reqs
        </a>
        <a className={includeCompleted ? 'toggle-active' : ''}
           onClick={() => setIncludeCompleted(!includeCompleted)}>
          {includeCompleted ? '✓ ' : ''}Include completed
        </a>
      </div>

      <div className="legend-bar">
        <span className="key calc">teal</span> calculated by GTrack
        <span className="key admin">purple</span> editable by admin only
      </div>

      {error && <div className="error-screen">{error}</div>}

      <table className="gtrack-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('priority')}>Pri{sortIndicator('priority')}</th>
            <th onClick={() => handleSort('project_created')}>Created{sortIndicator('project_created')}</th>
            <th onClick={() => handleSort('project_type')}>Type{sortIndicator('project_type')}</th>
            <th onClick={() => handleSort('request_category')}>Category{sortIndicator('request_category')}</th>
            <th onClick={() => handleSort('project_name')}>Project Name{sortIndicator('project_name')}</th>
            <th onClick={() => handleSort('current_status')}>Status{sortIndicator('current_status')}</th>
            <th onClick={() => handleSort('num_measurements')}># Meas{sortIndicator('num_measurements')}</th>
            <th onClick={() => handleSort('est_meas_time_days')}>Est Meas Time{sortIndicator('est_meas_time_days')}</th>
            <th className="admin" onClick={() => handleSort('meas_start_date')}>Meas Start{sortIndicator('meas_start_date')}</th>
            <th className="admin" onClick={() => handleSort('ta_report_completion_date')}>TA Report Done{sortIndicator('ta_report_completion_date')}</th>
            <th className="calc" onClick={() => handleSort('since_created')}>Since Created{sortIndicator('since_created')}</th>
            <th className="calc" onClick={() => handleSort('since_prereqs')}>Since Pre-Reqs{sortIndicator('since_prereqs')}</th>
            <th className="calc" onClick={() => handleSort('active_meas_time')}>Active Meas Time{sortIndicator('active_meas_time')}</th>
            <th className="calc" onClick={() => handleSort('avg_meas_time')}>Avg Meas Time{sortIndicator('avg_meas_time')}</th>
            <th className="no-sort">Action</th>
          </tr>
        </thead>
        <tbody>
          {displayed.map(p => {
            const m = p.metadata || {};
            const numMeas = parseFloat(m.num_measurements?.value);
            const estMeasTime = m.est_meas_time_days?.value;
            const measStart = m.meas_start_date?.value;
            const taReportDone = m.ta_report_completion_date?.value;

            const sinceCreated = daysBetween(p.project_created, measStart || new Date());
            const sincePrereqs = p.prereqs_completed_at
              ? daysBetween(p.prereqs_completed_at, measStart || new Date())
              : null;

            let activeMeasTime = null;
            if (measStart) {
              const intervalEnd = taReportDone || new Date();
              const intervalDays = daysBetween(measStart, intervalEnd);
              const holdDays = totalHoldDaysWithinInterval(p.holds || [], measStart, intervalEnd);
              activeMeasTime = Math.max(0, intervalDays - holdDays);
            }

            const avgMeasTime = (activeMeasTime !== null && numMeas > 0)
              ? (activeMeasTime / numMeas).toFixed(3)
              : null;

            const activeHold = (p.holds || []).find(h => !h.ended_at);
            const isOnHold = !!activeHold;
            const category = m.request_category?.value || '';
            const isUrgent = category === URGENT_CATEGORY;

            return (
              <tr
                key={p.id}
                className="clickable-row"
                onClick={() => { setSelectedProject(p); setShowDetails(true); }}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  {user.isAdmin
                    ? <PriorityCell projectId={p.id} value={p.priority} token={token} api={api} onSaved={load} />
                    : (p.priority || '')}
                </td>
                <td>{formatDate(p.project_created)}</td>
                <td title={getProjectTypeDisplay(p.project_type)}>{typeAbbrev(p.project_type)}</td>
                <td>
                  {isUrgent
                    ? <span className="badge-urgent" title={category}>Urgent</span>
                    : category}
                </td>
                <td>{p.project_name}</td>
                <td>
                  {isOnHold ? (
                    <span className="badge-onhold" title={`On hold since ${formatDate(activeHold.started_at)}`}>
                      On Hold ({p.active_hold_reason})
                    </span>
                  ) : (
                    p.current_status
                  )}
                </td>
                <td>{m.num_measurements?.value || ''}</td>
                <td>{estMeasTime ? `${estMeasTime}d` : ''}</td>
                <td className="admin">{measStart ? formatDate(measStart) : ''}</td>
                <td className="admin">{taReportDone ? formatDate(taReportDone) : ''}</td>
                <td className="calc">{sinceCreated !== null ? `${sinceCreated}d` : '—'}</td>
                <td className="calc">{sincePrereqs !== null ? `${sincePrereqs}d` : '—'}</td>
                <td className="calc">{activeMeasTime !== null ? `${activeMeasTime}d` : '—'}</td>
                <td className="calc">{avgMeasTime !== null ? `${avgMeasTime}d` : '—'}</td>
                <td className="action-cell" onClick={(e) => e.stopPropagation()}>
                  {user.isAdmin && (
                    isOnHold
                      ? <button className="btn" onClick={() => endHold(p.id, activeHold.id)}>Resume</button>
                      : <button className="btn" onClick={() => startHold(p.id)}>Hold</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filtered.length === 0 && (
        <div className="loading-screen">No thermal projects match the current filters.</div>
      )}

      <WorkflowModal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        project={selectedProject}
        user={{ ...user, token }}
        onProjectUpdate={load}
        api={api}
      />

      <NewProjectModal
        isOpen={showNewProject}
        onClose={() => setShowNewProject(false)}
        onProjectCreate={load}
        user={{ ...user, token }}
        api={api}
        /* Only the two queue types are offered here; default to whichever was created last if it is
           one of them (NewProjectModal remembers it), else TA. */
        typeOptions={THERMAL_QUEUE_TYPES}
        defaultType={THERMAL_QUEUE_TYPES.includes(localStorage.getItem('gtrack_last_project_type'))
          ? localStorage.getItem('gtrack_last_project_type')
          : THERMAL_QUEUE_TYPES[0]}
        onProjectCreated={(newProject) => {
          setSelectedProject(newProject);
          setShowDetails(true);
        }}
      />
    </div>
  );
};

export default TAQueueScreen;
